import { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

const SKILL_LEVELS = ['Unknown', 'Beginner', 'Intermediate', 'Advanced'];

export default function NewTrialModal({ onClose, onCreated }) {
  const { post } = useApi();
  const { currentClub } = useClub();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    startDate: new Date().toISOString().split('T')[0],
    skillLevel: 'Unknown',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dupBlock, setDupBlock] = useState(null);   // hard block
  const [dupWarn, setDupWarn] = useState(null);     // soft warn — staff can override
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      setError('Email or phone is required for dedup protection.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await post('/t2m/trial', {
        firstName:  form.firstName.trim(),
        lastName:   form.lastName.trim(),
        email:      form.email.trim().toLowerCase() || null,
        phone:      form.phone.trim() || null,
        startDate:  form.startDate,
        skillLevel: form.skillLevel,
        notes:      form.notes.trim() || null,
        locationId: currentClub.id,
      });

      // Hard block — active trial exists
      if (result.duplicate && result.severity === 'block') {
        setDupBlock(result);
        setSaving(false);
        return;
      }

      // Soft warn — previous trial history
      if (result.duplicate && result.severity === 'warn' && !overrideConfirmed) {
        setDupWarn(result);
        setSaving(false);
        return;
      }

      // Success
      onCreated(result.trialId || result.id);

    } catch (err) {
      setError(err.message || 'Failed to create trial. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Hard block UI
  if (dupBlock) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>🚫 Duplicate Trial Detected</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            <div className="alert-error" style={{ marginBottom: 16 }}>
              <strong>Active trial already exists</strong><br />
              {dupBlock.message}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              This member already has an active trial at {currentClub.name}. 
              Creating a duplicate is not allowed. Open the existing record instead.
            </p>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Existing record</div>
              <div style={{ fontWeight: 600 }}>{dupBlock.existingName}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Match type: {dupBlock.matchType === 'email' ? '📧 Email' : '📱 Phone'}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={() => onCreated(dupBlock.trialId)}>
              Open Existing Record
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Soft warn UI — previous trial
  if (dupWarn && !overrideConfirmed) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>⚠️ Previous Trial Found</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            <div className="alert-warning" style={{ marginBottom: 16 }}>
              {dupWarn.message}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              This member has used a trial before. You can still create a new trial, 
              but this will be flagged for manager review. Make sure this is approved 
              before proceeding.
            </p>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--orange)', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Previous trial record</div>
              <div style={{ fontWeight: 600 }}>{dupWarn.existingName}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Started: {dupWarn.previousDate} · Outcome: <span style={{ textTransform: 'capitalize' }}>{dupWarn.previousStatus}</span>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-secondary" onClick={() => onCreated(dupWarn.trialId)}>
              View Previous Record
            </button>
            <button className="btn-primary" onClick={() => {
              setOverrideConfirmed(true);
              setDupWarn(null);
            }}>
              Proceed Anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal form
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🌱 New Trial Member</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          {overrideConfirmed && (
            <div className="alert-warning" style={{ marginBottom: 16 }}>
              ⚠️ Override confirmed — previous trial history exists. This will be flagged for manager review.
            </div>
          )}

          <div className="form-row">
            <div className="form-field">
              <label className="field-label">First Name *</label>
              <input className="field-input" value={form.firstName}
                onChange={e => set('firstName', e.target.value)}
                placeholder="First name" autoFocus />
            </div>
            <div className="form-field">
              <label className="field-label">Last Name *</label>
              <input className="field-input" value={form.lastName}
                onChange={e => set('lastName', e.target.value)}
                placeholder="Last name" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="field-label">Email</label>
              <input className="field-input" type="email" value={form.email}
                onChange={e => set('email', e.target.value)}
                placeholder="email@example.com" />
            </div>
            <div className="form-field">
              <label className="field-label">Phone</label>
              <input className="field-input" type="tel" value={form.phone}
                onChange={e => set('phone', e.target.value)}
                placeholder="(414) 555-0101" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="field-label">Trial Start Date</label>
              <input className="field-input" type="date" value={form.startDate}
                onChange={e => set('startDate', e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">Skill Level</label>
              <select className="field-input" value={form.skillLevel}
                onChange={e => set('skillLevel', e.target.value)}>
                {SKILL_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-field">
            <label className="field-label">Notes</label>
            <textarea className="field-input" value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="How they heard about us, referral, etc."
              rows={3} style={{ resize: 'vertical' }} />
          </div>

          <div className="modal-hint">
            <span>🔒</span>
            <span>Email or phone is used to detect duplicate or repeat trial attempts — protecting against trial abuse.</span>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Checking…' : overrideConfirmed ? 'Create Trial (Override)' : 'Create Trial'}
          </button>
        </div>
      </div>
    </div>
  );
}
